import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { OdProtosBuyerV2Data_ReactionSource } from '__generated__/athena/graphql';
import { usePropertyReactions } from 'components/marketplace/helpers/usePropertyReactions';

import { COSMOS_URL } from '../../globals';
import { PropertyCard } from '../PropertyCard';
import { buildAddressLines, buildPropertyPhotoUrl, convertDwellingType } from './helpers';

// This hook is used to lazy load the photo URL when the component is in view
// It uses the Intersection Observer API to detect when the component is in the viewport
// and sets the photo URL to be loaded only then
// This helps in optimizing performance by not loading images that are not visible to the user
// and reduces the initial load time of the component
const useLazyLoadPhoto = (photoUrl: string | null) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || isLoaded) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isLoaded]);

  return { containerRef, loadedPhotoUrl: isLoaded ? photoUrl : null };
};

interface Tracking {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
}

export interface ListingCardData {
  addressToken?: string | null;
  baths?: number | null;
  beds?: number | null;
  city?: string | null;
  dwellingType?: string | null;
  fullAddress?: string | null;
  estimatedMonthlyPayment?: number | null;
  listPrice?: number | null;
  livingSquareFootage?: number | null;
  neighborhood?: string | null;
  slug?: string | null;
  state?: string | null;
  street?: string | null;
  photos?: string[] | null;
  isLiked?: boolean | null;
}

interface SimilarListingsProps {
  listings: ListingCardData[];
  title?: string | null;
}

interface ListingCardComponentProps {
  listing: ListingCardData;
}

export const ListingCard: React.FC<ListingCardComponentProps & Tracking> = ({
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
  listing,
}) => {
  const { isLiked, onClickReaction } = usePropertyReactions(
    listing?.addressToken,
    OdProtosBuyerV2Data_ReactionSource.REACTION_SOURCE_WEB_PDP,
    listing?.isLiked,
  );

  const photoUrl = buildPropertyPhotoUrl(listing?.photos?.[0], {
    preset: '3:2-md',
    presetScale: '2/3',
    dpr: '1',
  });

  const { containerRef, loadedPhotoUrl } = useLazyLoadPhoto(photoUrl);

  const link = `${COSMOS_URL}/properties/${listing?.slug ?? '_'}/aid_${
    listing?.addressToken
  }?${new URLSearchParams({
    ...(utmSource && { utm_source: utmSource }),
    ...(utmMedium && { utm_medium: utmMedium }),
    ...(utmCampaign && { utm_campaign: utmCampaign }),
    ...(utmContent && { utm_content: utmContent }),
    ...(utmTerm && { utm_term: utmTerm }),
  }).toString()}`;

  const { primaryLine, secondaryLine } = buildAddressLines(listing);

  return (
    <Col ref={containerRef} className="w-full">
      <PropertyCard
        baths={listing?.baths}
        beds={listing?.beds}
        city={secondaryLine}
        dwellingType={convertDwellingType(listing?.dwellingType)}
        estimatedMonthlyPayment={listing?.estimatedMonthlyPayment}
        href={link}
        isFavorite={isLiked}
        neighborhood={primaryLine}
        onFavoritePress={onClickReaction}
        photoUrl={loadedPhotoUrl}
        price={listing?.listPrice}
        sqft={listing?.livingSquareFootage}
      />
    </Col>
  );
};

const DISPLAYED_LISTING_SET_COUNT = 6;

export const SimilarListings: React.FC<SimilarListingsProps & Tracking> = ({
  listings,
  title,
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
}) => {
  const [displayedListingCount, setDisplayedListingCount] = React.useState(
    DISPLAYED_LISTING_SET_COUNT,
  );

  const incrementDisplayedListingCount = () => {
    setDisplayedListingCount((prev) => prev + DISPLAYED_LISTING_SET_COUNT);
  };

  const similarListings = useMemo(() => {
    return listings
      .filter((_, index) => index < displayedListingCount)
      .map((listing, index) => {
        return (
          <Col key={index} className="w-full pb-6 lg:w-[48%]">
            <ListingCard
              listing={listing}
              utmSource={utmSource}
              utmMedium={utmMedium}
              utmCampaign={utmCampaign}
              utmContent={utmContent}
              utmTerm={utmTerm}
            />
          </Col>
        );
      });
  }, [listings, displayedListingCount]);

  return (
    <Col className="pb-31 md:pb-6">
      <Text as="h5" className="pb-6 text-subheader-xs text-od-content-primary">
        {title || 'Similar listings'}
      </Text>
      <Col className="flex-wrap justify-between lg:flex-row">{similarListings}</Col>
      {displayedListingCount < listings.length && (
        <Button
          onClick={incrementDisplayedListingCount}
          variant="secondary"
          size="lg"
          analyticsName="marketplace-show-more-listings"
          aria-label="See more homes"
          className="w-full"
        >
          See more homes
        </Button>
      )}
    </Col>
  );
};
